import { reflectKeys } from "@utils/key.reflactor.ts";

export const NAMESPACE = {
  AUTH: "auth",
  APP: "app",
};

export const MODE = {
  EDIT: "EDIT",
  VIEW: "VIEW",
};

export const ROLE_IN_GROUP = {
  GROUP_OWNER: "GROUP_OWNER",
  CO_TEACHER: "CO_TEACHER",
  LIAISON_TEACHER: "LIAISON_TEACHER",
  OBSERVER: "OBSERVER",
  STUDENT: "STUDENT", // only for front end usage as there is no such constant in the backend.
} as const;

export const MAX_LENGTH = {
  GROUP_NAME: 50,
  CATEGORY_NAME: 50,
  CATEGORY_TOTAL: 100,
  CAROUSEL_SIZE: 9,
  TITLE: 250,
};

export const ERROR_CODE = {
  DUPLICATED_NAME: "duplicated-name",
  ABSENT_TEACHER: "absent-teacher",
  NO_OWNER: "teaching.group.with.no.owner",
};

export const CHECK_ASSIGNMENT_STATUS = reflectKeys(["OFF", "LOADING", "COMPLETE"]);
export const ASSIGNMENT_AVAILABILITY = reflectKeys([
  "AVAILABLE",
  "NOT_AVAILABLE",
  "NOT_APPLICABLE",
]);

export const ALL_OPTION = "All";

export const SUBJECT_OPTIONS = {
  MY_SUBJECTS: {
    id: 0,
    subjectName: "student_group_group_detail_subpage_subject_details_my_subjects_label",
    key: "MINE", // value that returned from backend
  },
  FOLLOWED_SUBJECTS: {
    id: 0,
    subjectName: "student_group_group_detail_subpage_subject_details_followed_subjects_label",
    key: "FOLLOWED", // value that returned from backend
  },
  OTHER_SUBJECTS: {
    id: 0,
    subjectName: "student_group_group_detail_subpage_subject_details_other_subjects_label",
    key: "OTHER", // value that returned from backend
  },
};

export const REDIRECT_DELAY_MS = 2000;

export const LEVEL_AND_COURSE_OPTIONS = {
  MY_LEVELS: {
    id: 0,
    displayName: "student_group_group_detail_subpage_subject_details_my_levels_label",
    key: "myLevelAndStreams", // value that returned from backend
  },
  FOLLOWED_LEVELS: {
    id: 0,
    displayName: "student_group_group_detail_subpage_subject_details_followed_levels_label",
    key: "followedLevelAndStreams", // value that returned from backend
  },
  OTHER_LEVELS: {
    id: 0,
    displayName: "student_group_group_detail_subpage_subject_details_other_levels_label",
    key: "otherLevelAndStreams", // value that returned from backend
  },
};

export const GROUP_ROLE_OPTIONS = {
  ALL: { value: "ALL", label: "student_group_list_role_filter_option_all_label" },
  GROUP_OWNER_CO_TEACHER: {
    value: "GROUP_OWNER_CO_TEACHER",
    label: "student_group_list_role_filter_option_owner_co_teacher_label",
  },
  OBSERVER: { value: "OBSERVER", label: "student_group_list_role_filter_option_observer_label" },
  LIAISON_TEACHER: {
    value: "LIAISON_TEACHER",
    label: "student_group_list_role_filter_option_liaison_teacher_label",
  },
};

export const PAST_GROUP_ROLE_OPTIONS = {
  ALL: { value: "ALL", label: "student_group_list_role_filter_option_all_label" },
  GROUP_OWNER_CO_TEACHER: {
    value: "GROUP_OWNER_CO_TEACHER",
    label: "student_group_list_role_filter_option_owner_co_teacher_label",
  },
  OBSERVER: { value: "OBSERVER", label: "student_group_list_role_filter_option_observer_label" },
};

export const DEFAULT_CATEGORY_NAME = "Untitled Category";

export const RESOURCE_TYPE = {
  ASSIGNMENT: "ASSIGNMENT",
  CLASS_GROUP_RESOURCE: "CLASS_GROUP_RESOURCE",
};

export const INDEX_INSERT_ACTION_TYPE = {
  BEFORE: "BEFORE",
  AFTER: "AFTER",
  NONE: "NONE",
};

export const MAX_CLASS_GROUPS_PIN_LIMIT = 12;

export const CLASS_GROUP_CALENDAR_INIT_VIEW_LOCAL_STORAGE_KEY = "class-group-calendar-view-init-";

export const GROUP_FUNCTION_TYPE_OPTIONS = {
  STUDENT: {
    label: "student_group_group_detail_subpage_groupfunctiontype_student_option_label",
    value: "STUDENT",
  },
  PROFESSIONAL_DEVELOPMENT: {
    label: "student_group_group_detail_subpage_groupfunctiontype_pd_option_label",
    value: "PROFESSIONAL_DEVELOPMENT",
  },
  STAFF: {
    label: "student_group_group_detail_subpage_groupfunctiontype_staff_option_label",
    value: "STAFF",
  },
  COMMUNITY: {
    label: "student_group_group_detail_subpage_groupfunctiontype_community_option_label",
    value: "COMMUNITY",
  },
};

export const GROUP_FUNCTION_TYPE = {
  STUDENT: "STUDENT",
  PROFESSIONAL_DEVELOPMENT: "PROFESSIONAL_DEVELOPMENT",
  STAFF: "STAFF",
  COMMUNITY: "COMMUNITY",
} as const;

export const GROUP_FUNCTION_TYPE_FILTER_OPTIONS = {
  ALL: {
    label: "student_group_group_detail_subpage_groupfunctiontype_all_option_label",
    value: "ALL",
  },
  STUDENT: {
    label: "student_group_group_detail_subpage_groupfunctiontype_filter_student_option_label",
    value: "STUDENT",
  },
  PROFESSIONAL_DEVELOPMENT: {
    label: "student_group_group_detail_subpage_groupfunctiontype_filter_pd_option_label",
    value: "PROFESSIONAL_DEVELOPMENT",
  },
  STAFF: {
    label: "student_group_group_detail_subpage_groupfunctiontype_filter_staff_option_label",
    value: "STAFF",
  },
};

export const GROUP_DETAILS_FUNCTION_TYPE_OPTIONS = {
  STUDENT: {
    label: "student_group_group_detail_subpage_groupfunctiontype_student_option_label",
    value: "STUDENT",
  },
  PROFESSIONAL_DEVELOPMENT: {
    label: "student_group_group_detail_subpage_groupfunctiontype_pd_option_label",
    value: "PROFESSIONAL_DEVELOPMENT",
  },
  STAFF: {
    label: "student_group_group_detail_subpage_groupfunctiontype_staff_option_label",
    value: "STAFF",
  },
  COMMUNITY: {
    label: "student_group_group_detail_subpage_groupfunctiontype_convert_to_community_option_label",
    value: "COMMUNITY",
  },
};

export const FILE_SIZE_LIMIT = "10";

export const STUDENT_GROUP_NOTIFICATION_POLLING_INTERVAL = 60000;

export const STUDENT_GROUP_MEMBERSHIP_STATUS = reflectKeys(["NOT_REQUESTED", "PENDING"]);

export const STUDENT_GROUP_PENDING_SUBPAGE_TABS = reflectKeys(["STUDENTS", "TEACHERS"]);

export const BROWSE_COMMUNITY_SORT_OPTIONS = [
  {
    value: "id",
    label: "student_group_browse_community_sort_option_latest",
  },
  {
    value: "score",
    label: "student_group_browse_community_sort_option_relevance",
  },
  {
    value: "membershipCount",
    label: "student_group_browse_community_sort_option_member_count",
  },
];

export const BROWSE_COMMUNITY_BASE_PAYLOAD = {
  keyword: "",
  cacheable: false,
  draw: 0,
  start: 0,
  length: 12,
  sortColumn: "",
  sortDirection: "",
  sortFields: [],
  sortDirections: [],
  Filters: {},
  advancedFilter: false,
};

export const ACCOUNT_STATUS = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  LOCKED: "Locked",
};

export const STUDENT_GROUP_MEMBER_LISTING_TAB = {
  TEACHER: "TEACHER",
  STUDENT: "STUDENT",
};

export const STUDENT_GROUP_TYPE = {
  SUBJECT_GROUP: "SUBJECT_GROUP",
  TEACHING_GROUP: "TEACHING_GROUP",
} as const;

export const GROUP_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
} as const;

export const TAB = {
  CURRENT: "CURRENT",
  PAST: "PAST",
} as const;

export const MANAGED_BY = {
  GEB: "GEB",
  SC: "SC",
} as const;

export const GROUP_VISIBILITY_FILTER_OPTIONS = {
  VISIBLE: {
    value: "VISIBLE",
    label: "student_group_list_visibility_filter_visible_label",
  },
  HIDDEN: {
    value: "HIDDEN",
    label: "student_group_list_visibility_filter_hidden_label",
  },
} as const;
